import React, { createContext, useContext, useEffect, useState } from 'react';
import Select from 'react-select';
import { IoIosArrowDropdown, IoIosArrowDropup } from 'react-icons/io';
import {
  LuGripVertical,
  LuPencil,
  LuTrash2,
  LuPlus,
  LuMoreHorizontal,
  LuCheck,
  LuAlertTriangle,
} from 'react-icons/lu';
import { NODE_THEMES } from './NodeTheme';

// Primitivos compartidos por los formularios de los 11 paneles de edición.
// Antes cada panel repetía a mano labels grises (#6C757D), secciones con borde
// #b6b6b6 y botones sólidos; eso vivía duplicado y se saltaba el tema.
// NodeEditorChrome provee {theme, color}, así que los primitivos no necesitan props.
const NodeFormContext = createContext({
  theme: NODE_THEMES.light,
  color: '#5BC0DE',
});

export const NodeFormProvider = NodeFormContext.Provider;
export const useNodeForm = () => useContext(NodeFormContext);

// Label de campo: sustituye a los <label style={{ color: '#6C757D' }}> inline.
export const FieldLabel = ({ htmlFor, children, style, ...rest }) => {
  const { theme } = useNodeForm();
  return (
    <label
      htmlFor={htmlFor}
      style={{
        display: 'block',
        fontSize: '12.5px',
        fontWeight: 600,
        color: theme.textSecondary,
        marginBottom: '5px',
        ...style,
      }}
      {...rest}
    >
      {children}
    </label>
  );
};

// Sección agrupadora: reemplaza los contenedores con borde #b6b6b6.
export const FormSection = ({ title, children, style }) => {
  const { theme } = useNodeForm();
  return (
    <div
      style={{
        border: `1px solid ${theme.border}`,
        borderRadius: '10px',
        padding: '12px',
        backgroundColor: theme.inputBg,
        ...style,
      }}
    >
      {title && (
        <div
          style={{
            fontSize: '12px',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: theme.textSecondary,
            marginBottom: '10px',
          }}
        >
          {title}
        </div>
      )}
      {children}
    </div>
  );
};

// Botón de acción secundaria (Agregar, Gestionar...): chip tintado con el color
// del tipo, no un bloque sólido. El sólido queda reservado para "Guardar".
// tone="danger" para acciones destructivas (Eliminar multimedia); "full" ocupa
// todo el ancho.
export const ChipButton = ({
  onClick,
  icon: Icon,
  children,
  disabled = false,
  tone = 'brand',
  full = false,
}) => {
  const { color } = useNodeForm();
  const [hover, setHover] = useState(false);
  const tint = tone === 'danger' ? '#FF2242' : color;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: full ? 'flex' : 'inline-flex',
        width: full ? '100%' : undefined,
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '8px 14px',
        border: `1px solid color-mix(in srgb, ${tint} 35%, var(--su-surface, #ffffff))`,
        borderRadius: '8px',
        backgroundColor: hover ? `color-mix(in srgb, ${tint} 20%, var(--su-surface, #ffffff))` : `color-mix(in srgb, ${tint} 12%, var(--su-surface, #ffffff))`,
        color: tint,
        fontSize: '12px',
        fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background-color 0.15s ease',
      }}
    >
      {Icon && <Icon style={{ fontSize: '14px' }} />}
      {children}
    </button>
  );
};

// Tile selector de multimedia (imagen, video, documento, audio). Reemplaza los
// bloques con degradado verde y texto blanco por una superficie sutil con el
// icono en el color del tipo. Envuelve el <input type=file> oculto (children);
// la etiqueta usa htmlFor para dispararlo.
export const MediaTile = ({ icon: Icon, label, htmlFor, children }) => {
  const { theme, color } = useNodeForm();
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        borderRadius: '10px',
        padding: '10px 8px',
        cursor: 'pointer',
        border: `1px solid ${hover ? color : theme.border}`,
        backgroundColor: hover ? `color-mix(in srgb, ${color} 8%, var(--su-surface, #ffffff))` : theme.inputBg,
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
      }}
    >
      {children}
      <label
        htmlFor={htmlFor}
        className="pointer m-0 nfk-accent-label"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '5px',
          color,
          cursor: 'pointer',
        }}
      >
        <Icon style={{ fontSize: '20px' }} />
        <span style={{ fontSize: '12px', fontWeight: 600 }}>{label}</span>
      </label>
    </div>
  );
};

// Fila de un item de lista (condición, botón interactivo, header de API...).
const RowIconButton = ({ title, onClick, disabled, children }) => {
  const { theme } = useNodeForm();
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '22px',
        height: '22px',
        flexShrink: 0,
        padding: 0,
        border: 'none',
        borderRadius: '6px',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.35 : 1,
        backgroundColor: hover && !disabled ? `${theme.border}` : 'transparent',
        color: theme.textTertiary,
        fontSize: '12px',
        lineHeight: 1,
      }}
    >
      {children}
    </button>
  );
};

export const ListRow = ({
  children,
  onRemove,
  onEdit,
  dragIndex = null,
  onReorder,
  revealActions = false,
  removeTitle = 'Eliminar',
  editTitle = 'Editar',
  style,
}) => {
  const { theme, color } = useNodeForm();
  const [hover, setHover] = useState(false);
  const [rowHover, setRowHover] = useState(false);
  const [dropSide, setDropSide] = useState(null);
  const sortable = dragIndex !== null && typeof onReorder === 'function';
  const showActions = !revealActions || rowHover;

  return (
    <div
      draggable={sortable}
      onMouseEnter={() => setRowHover(true)}
      onMouseLeave={() => setRowHover(false)}
      onDragStart={
        sortable
          ? (event) => {
              event.dataTransfer.effectAllowed = 'move';
              event.dataTransfer.setData('text/plain', String(dragIndex));
            }
          : undefined
      }
      onDragOver={
        sortable
          ? (event) => {
              event.preventDefault();
              const box = event.currentTarget.getBoundingClientRect();
              setDropSide(
                event.clientY < box.top + box.height / 2 ? 'top' : 'bottom'
              );
            }
          : undefined
      }
      onDragLeave={sortable ? () => setDropSide(null) : undefined}
      onDrop={
        sortable
          ? (event) => {
              event.preventDefault();
              const from = Number(event.dataTransfer.getData('text/plain'));
              const side = dropSide;
              setDropSide(null);
              if (Number.isNaN(from) || from === dragIndex) return;
              let to = dragIndex;
              if (side === 'bottom' && from > dragIndex) to = dragIndex + 1;
              if (side === 'top' && from < dragIndex) to = dragIndex - 1;
              onReorder(from, Math.max(0, to));
            }
          : undefined
      }
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 10px',
        marginBottom: '6px',
        borderRadius: '8px',
        border: `1px solid ${rowHover ? theme.borderStrong || theme.border : theme.border}`,
        borderTop:
          dropSide === 'top'
            ? `2px solid ${color}`
            : `1px solid ${rowHover ? theme.borderStrong || theme.border : theme.border}`,
        borderBottom:
          dropSide === 'bottom'
            ? `2px solid ${color}`
            : `1px solid ${rowHover ? theme.borderStrong || theme.border : theme.border}`,
        backgroundColor: rowHover ? theme.inputBg : theme.panelBg,
        color: theme.textPrimary,
        fontSize: '12px',
        transition: 'background-color 0.12s ease, border-color 0.12s ease',
        ...style,
      }}
    >
      {sortable && (
        <span
          title="Arrastrar para reordenar"
          style={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            cursor: 'grab',
            color: rowHover ? color : theme.textTertiary,
            fontSize: '13px',
            lineHeight: 1,
          }}
        >
          <LuGripVertical />
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
      {onEdit && showActions && (
        <RowIconButton title={editTitle} onClick={onEdit}>
          <LuPencil />
        </RowIconButton>
      )}
      {onRemove && !showActions && (
        <span style={{ width: '22px', flexShrink: 0 }} />
      )}
      {onRemove && showActions && (
        <button
          type="button"
          title={removeTitle}
          onClick={onRemove}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '22px',
            height: '22px',
            flexShrink: 0,
            padding: 0,
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            backgroundColor: hover ? '#FF224220' : 'transparent',
            color: hover ? '#FF2242' : theme.textTertiary,
            transition: 'background-color 0.15s ease, color 0.15s ease',
          }}
        >
          <LuTrash2 />
        </button>
      )}
    </div>
  );
};

export const SegmentedTabs = ({ value, onChange, options = [] }) => {
  const { theme, color } = useNodeForm();
  return (
    <div
      style={{
        display: 'flex',
        gap: '2px',
        padding: '3px',
        borderRadius: '9px',
        backgroundColor: theme.inputBg,
        border: `1px solid ${theme.border}`,
      }}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            title={option.disabled ? option.disabledHint : undefined}
            disabled={option.disabled}
            onClick={() => !option.disabled && onChange(option.value)}
            style={{
              flex: 1,
              minWidth: 0,
              padding: '6px 8px',
              border: 'none',
              borderRadius: '7px',
              fontSize: '12px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              backgroundColor: active ? `color-mix(in srgb, ${color} 15%, var(--su-surface, #ffffff))` : 'transparent',
              color: active ? color : theme.textTertiary,
              cursor: option.disabled ? 'not-allowed' : 'pointer',
              opacity: option.disabled ? 0.4 : 1,
              transition: 'background-color 0.15s ease, color 0.15s ease',
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export const ToggleField = ({
  id,
  label,
  hint,
  checked,
  onChange,
  disabled,
  last = false,
}) => {
  const { theme, color } = useNodeForm();
  const [hover, setHover] = useState(false);
  const on = Boolean(checked);

  return (
    <div
      onClick={() => !disabled && onChange(!on)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '11px 0',
        borderBottom: last ? 'none' : `1px solid ${theme.border}`,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: theme.textPrimary,
            lineHeight: 1.4,
          }}
        >
          {label}
        </div>
        {hint && (
          <div
            style={{
              fontSize: '12px',
              lineHeight: 1.45,
              color: theme.textTertiary,
              marginTop: '3px',
            }}
          >
            {hint}
          </div>
        )}
      </div>

      <span
        id={id}
        role="switch"
        aria-checked={on}
        style={{
          position: 'relative',
          width: '36px',
          height: '20px',
          flexShrink: 0,
          marginTop: '1px',
          borderRadius: '10px',
          backgroundColor: on
            ? color
            : hover
            ? theme.borderStrong || theme.border
            : theme.border,
          transition: 'background-color 0.18s ease',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: '2px',
            left: on ? '18px' : '2px',
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.35)',
            transition: 'left 0.18s ease',
          }}
        />
      </span>
    </div>
  );
};

export const boolOption = (checked) => ({
  label: checked ? 'Si' : 'No',
  id: checked,
});

export const StepSection = ({
  index,
  title,
  summary,
  state = 'pending',
  children,
}) => {
  const { theme, color } = useNodeForm();
  const done = state === 'done';
  const error = state === 'error';
  const tint = error ? '#FF2242' : color;

  return (
    <div
      style={{
        marginBottom: '14px',
        overflow: 'hidden',
        borderRadius: '12px',
        border: `1px solid ${error ? '#FF224259' : theme.border}`,
        backgroundColor: theme.panelBg,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '9px',
          padding: '10px 12px',
          borderBottom: `1px solid ${theme.border}`,
          backgroundColor: theme.inputBg,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '22px',
            height: '22px',
            flexShrink: 0,
            borderRadius: '50%',
            backgroundColor: done || error ? `color-mix(in srgb, ${tint} 15%, var(--su-surface, #ffffff))` : 'transparent',
            border:
              done || error
                ? 'none'
                : `1px solid ${theme.borderStrong || theme.border}`,
            color: done || error ? tint : theme.textTertiary,
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          {done ? <LuCheck /> : error ? <LuAlertTriangle /> : index}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '0.02em',
              color: theme.textPrimary,
            }}
          >
            {title}
          </div>
          {summary && (
            <div
              style={{
                fontSize: '12px',
                marginTop: '1px',
                color: error ? '#FF2242' : theme.textTertiary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {summary}
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: '14px 12px' }}>{children}</div>
    </div>
  );
};

export const SectionToolbar = ({
  addLabel,
  onAdd,
  actions = [],
}) => {
  const { theme, color } = useNodeForm();
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  const available = actions.filter((action) => action && !action.hidden);

  return (
    <div style={{ position: 'relative', marginBottom: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
        <ChipButton icon={LuPlus} full onClick={onAdd}>
          {addLabel}
        </ChipButton>
        {available.length > 0 && (
          <button
            type="button"
            title="Más acciones"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={(event) => {
              event.stopPropagation();
              setOpen((prev) => !prev);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '34px',
              height: '34px',
              flexShrink: 0,
              padding: 0,
              borderRadius: '8px',
              border: `1px solid ${theme.borderStrong || theme.border}`,
              backgroundColor: open || hover ? theme.inputBg : 'transparent',
              color: open ? color : theme.textSecondary,
              cursor: 'pointer',
              transition: 'background-color 0.15s ease, color 0.15s ease',
            }}
          >
            <LuMoreHorizontal style={{ fontSize: '15px' }} />
          </button>
        )}
      </div>

      {open && (
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            position: 'absolute',
            right: 0,
            top: '40px',
            zIndex: 40,
            minWidth: '220px',
            overflow: 'hidden',
            borderRadius: '10px',
            border: `1px solid ${theme.borderStrong || theme.border}`,
            backgroundColor: theme.panelBg,
            boxShadow: '0 12px 28px rgba(0, 0, 0, 0.4)',
          }}
        >
          {available.map((action) => (
            <SectionMenuItem
              key={action.label}
              action={action}
              onDone={() => setOpen(false)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SectionMenuItem = ({ action, onDone }) => {
  const { theme } = useNodeForm();
  const [hover, setHover] = useState(false);
  const Icon = action.icon;
  const danger = action.tone === 'danger';
  const tint = danger ? '#FF2242' : theme.textSecondary;

  return (
    <button
      type="button"
      disabled={action.disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => {
        onDone();
        action.onClick?.();
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '9px',
        width: '100%',
        padding: '9px 11px',
        border: 'none',
        textAlign: 'left',
        backgroundColor: hover && !action.disabled ? theme.inputBg : 'transparent',
        color: theme.textPrimary,
        cursor: action.disabled ? 'default' : 'pointer',
        opacity: action.disabled ? 0.4 : 1,
      }}
    >
      {Icon && <Icon style={{ fontSize: '14px', color: tint, flexShrink: 0 }} />}
      <span style={{ minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: 600,
            color: danger ? '#FF2242' : theme.textPrimary,
          }}
        >
          {action.label}
        </span>
        {action.description && (
          <span
            style={{
              display: 'block',
              fontSize: '12px',
              color: theme.textTertiary,
              marginTop: '1px',
            }}
          >
            {action.description}
          </span>
        )}
      </span>
    </button>
  );
};

// Aviso informativo dentro de un formulario. Sustituye los bloques
// rgba(0, 168, 132, 0.3) hardcodeados en los paneles.
export const InfoCallout = ({ title, children }) => {
  const { theme, color } = useNodeForm();
  return (
    <div
      style={{
        display: 'flex',
        gap: '8px',
        padding: '9px 11px',
        marginTop: '8px',
        borderRadius: '8px',
        borderLeft: `3px solid ${color}`,
        backgroundColor: `color-mix(in srgb, ${color} 8%, var(--su-surface, #ffffff))`,
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: '12px',
          lineHeight: 1.5,
          color: theme.textSecondary,
        }}
      >
        {title && (
          <span style={{ fontWeight: 600, color: theme.textPrimary }}>
            {title}{' '}
          </span>
        )}
        {children}
      </p>
    </div>
  );
};

export const CollapsibleSection = ({
  title,
  count = 0,
  open,
  onToggle,
  children,
}) => {
  const { theme, color } = useNodeForm();
  return (
    <div className="panel-group collapsible">
      <div className="panel panel-default">
        <div
          className="panel-heading p-2"
          style={{
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            backgroundColor: theme.inputBg,
            color: theme.textPrimary,
          }}
        >
          <div
            className="panel-title d-flex justify-content-between align-items-center pointer"
            onClick={onToggle}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minWidth: 0,
              }}
            >
              <span style={{ minWidth: 0 }}>{title}</span>
              {count > 0 && (
                <span
                  style={{
                    flexShrink: 0,
                    padding: '2px 8px',
                    borderRadius: '9px',
                    backgroundColor: `color-mix(in srgb, ${color} 15%, var(--su-surface, #ffffff))`,
                    color,
                    fontSize: '12px',
                    fontWeight: 600,
                    lineHeight: 1.4,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {count}
                </span>
              )}
            </span>
            {open ? (
              <IoIosArrowDropup
                className="pointer mr-3"
                style={{ fontSize: '20px' }}
              />
            ) : (
              <IoIosArrowDropdown
                className="pointer mr-3"
                style={{ fontSize: '20px' }}
              />
            )}
          </div>
        </div>
        {open && (
          <div className="panel-collapse pt-2" style={{ borderRadius: '4px' }}>
            <div className="panel-body" style={{ paddingInline: '0px' }}>
              {children}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const focusFirstInvalidField = (formErrors) => {
  const [firstName] = Object.keys(formErrors || {});
  if (!firstName) return;

  setTimeout(() => {
    const field = document.querySelector(
      `.node-editor-body [name="${firstName}"]`
    );
    if (!field) return;
    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof field.focus === 'function') field.focus({ preventScroll: true });
  }, 80);
};

// Estado vacío para listas sin items.
export const EmptyHint = ({ children }) => {
  const { theme } = useNodeForm();
  return (
    <div
      style={{
        padding: '12px',
        textAlign: 'center',
        fontSize: '12px',
        color: theme.textTertiary,
      }}
    >
      {children}
    </div>
  );
};

export const KitSelect = ({ value, onChange, options, theme, ariaLabel }) => (
  <Select
    aria-label={ariaLabel}
    value={options.find((option) => String(option.value) === String(value ?? '')) || null}
    options={options}
    isSearchable={options.length > 6}
    placeholder="Seleccionar"
    noOptionsMessage={() => 'Sin opciones'}
    menuPortalTarget={document.body}
    onChange={(option) => onChange(option.value)}
    styles={{
      control: (provided, state) => ({ ...provided, minHeight: '40px', backgroundColor: theme.cardBg, border: `1px solid ${state.isFocused ? '#5DCCED' : theme.border}`, borderRadius: '10px', boxShadow: 'none', fontSize: '14px', cursor: 'pointer', '&:hover': { borderColor: theme.borderStrong || theme.border } }),
      menuPortal: (provided) => ({ ...provided, zIndex: 1400 }),
      menu: (provided) => ({ ...provided, backgroundColor: theme.panelBg, border: `1px solid ${theme.border}`, borderRadius: '12px', overflow: 'hidden' }),
      menuList: (provided) => ({ ...provided, padding: '6px' }),
      option: (provided, state) => ({ ...provided, borderRadius: '8px', fontSize: '14px', cursor: 'pointer', color: state.isSelected ? theme.accent : theme.textPrimary, backgroundColor: state.isSelected || state.isFocused ? theme.inputBg : 'transparent' }),
      singleValue: (provided) => ({ ...provided, color: theme.textPrimary }),
      input: (provided) => ({ ...provided, color: theme.textPrimary }),
      placeholder: (provided) => ({ ...provided, color: theme.textTertiary }),
      indicatorSeparator: () => ({ display: 'none' }),
      dropdownIndicator: (provided) => ({ ...provided, color: theme.textSecondary, padding: '6px' }),
    }}
  />
);
